import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
}                                from '@angular/core';
import { BaseComponent }         from '@app/core/bases/base-component/base.component';
import {
	IListOperationsConfig,
	ListOperationsEnum,
	ListOperationsIdentifierEnum,
	ListOperationsResetEnum,
	ListOperationsResetModeEnum,
	ListOperationsResetType
}                                from '@shared/list-operations/model/list-operations.model';
import { ISearchData }           from '@shared/list-operations/model/search.model';
import { ListOperationsService } from '@shared/list-operations/services/list-operations.service';
import {
	Subject,
	Subscription
}                                from 'rxjs';
import {
	debounceTime,
	take,
	takeUntil
}                                from 'rxjs/operators';

@Component( {
	selector: 'app-search',
	templateUrl: './search.component.html',
	styleUrls: [ './search.component.scss' ]
} )

export class SearchComponent extends BaseComponent implements OnInit, OnDestroy {

	private _listOperationIdentifier: ListOperationsIdentifierEnum;

	private _revertValue: string;

	private _debounceTimer = 400;

	private _minSearchLength = 3;

	private _searchSubject: Subject<string> = new Subject();

	private _resetListOperationsSubscription: Subscription;

	private _trimmedSearch: string;

	activeOperation: boolean;

	search: string;

	isFocused: boolean;

	showRevertButton: boolean;

	showClearButton: boolean;

	showBody: boolean;

	placeholderI18nPath: string;

	@Input( 'activeListOperationChange' )
	set activeListOperationChange( activeListOperation: ListOperationsEnum ) {
		setTimeout( () => {
			if (
				activeListOperation !== ListOperationsEnum.SEARCH && !this.search
			) { this.showBody = false; }
		} );
	}

	@Input( 'defaultSearchData' )
	set defaultSearchData( defaultSearchData: ISearchData ) {
		this.setSearch( defaultSearchData );
		this.initialize();
		this.operationInitializedEmitter.emit( this.prepareSearchForEmit );
	}

	@Input( 'listOperationIdentifier' )
	set listOperationIdentifier( listOperationIdentifier: ListOperationsIdentifierEnum ) {

		this._resetListOperationsSubscription?.unsubscribe();

		this._listOperationIdentifier = listOperationIdentifier;

		this._resetListOperationsSubscription =
			this.listOperationsService.select.resetListOperationsData(
				listOperationIdentifier,
				'SearchComponent:set listOperationsIdentifier'
			)
			.pipe( takeUntil( this.ngUnsubscribe )
			)
			.subscribe( ( resetData: ListOperationsResetType ) => {
				if (
					resetData?.listOperator === ListOperationsResetEnum.ALL ||
					resetData?.listOperator === ListOperationsResetEnum.SEARCH
				) {
					switch ( resetData?.resetMode ) {
						case ListOperationsResetModeEnum.CLEAR:
							if ( this.search ) { this.clearControlValue(); }
							break;

						case ListOperationsResetModeEnum.DEFAULT:
						case ListOperationsResetModeEnum.RESTORE:
							this.resetSearch( resetData?.resetMode );
					}
				}
			} );
	}

	// TODO temporary flag prop to distinguish between old and new, will be removed when old has been completely replaced by new
	@Input() sidebar: boolean;

	@Output() operationChangedEmitter: EventEmitter<string[]> = new EventEmitter();

	@Output() operationInitializedEmitter: EventEmitter<string[]> = new EventEmitter();

	@Output() activeListOperationEmitter: EventEmitter<ListOperationsEnum> = new EventEmitter();

	@ViewChild( 'searchInput' ) searchInput: ElementRef;

	constructor(
		private readonly listOperationsService: ListOperationsService
	) {
		super();
		this.setSearchSubscription();
	}

	private updateActionButtonsVisibility(): void {
		const timeout = setTimeout( () => {
			clearTimeout( timeout );
			if ( this._trimmedSearch ) {
				this._revertValue = this._trimmedSearch;
				this.showClearButton = true;
				this.activeOperation = true;
			} else {
				this.showClearButton = false;
				this.activeOperation = false;
			}
			this.showRevertButton = this._revertValue
															? !this.showClearButton
															: false;
		}, 0 );
	}


	get prepareSearchForEmit(): string[] {
		return this._trimmedSearch
					 ? this._trimmedSearch.split( ' ' )
					 : [];
	}

	private setSearch( searchData: ISearchData ): void {

		if ( searchData ) {
			this.search = searchData?.values?.join( ' ' ) ?? null;
			this._trimmedSearch = this.search?.trim();
			this.placeholderI18nPath = searchData?.placeholderI18nPath;

			if ( this.search ) {
				this.updateActionButtonsVisibility();
				this.toggleBodyVisibility();
			}
		}
	}

	private setSearchSubscription(): void {

		this._searchSubject.pipe(
			debounceTime( this._debounceTimer )
		)
		.subscribe( () => {
			if ( this.search === null ) { return; }
			this.operationChangedEmitter.emit( this.prepareSearchForEmit );
		} );
	}

	private initialize(): void {
		this.activeOperation = this.search && this.search.length > 0;
		this._revertValue = this._trimmedSearch;
		this.isFocused = false;
	}

	private resetSearch( resetMode: ListOperationsResetModeEnum ): void {

		// gets default config from store because otherwise restore data may be used
		this.listOperationsService.select.configData(
			this._listOperationIdentifier,
			'SearchComponent:resetToDefaultControlValue'
		)
		.pipe(
			take( 1 ),
			takeUntil( this.ngUnsubscribe )
		)
		.subscribe( ( configData: IListOperationsConfig ) => {

			switch ( resetMode ) {
				case ListOperationsResetModeEnum.DEFAULT:
					this.setSearch( configData.defaultData?.searchData );
					break;
				case ListOperationsResetModeEnum.RESTORE:
					this.setSearch( configData.restoreData?.searchData );
					break;
			}
		} );

		this.onInputChange( this.search );
	}

	private emitSearchChanges(): void {
		this.updateActionButtonsVisibility();
		if (
			this._trimmedSearch?.length >= this._minSearchLength ||
			this._revertValue && this.search.length === 0
		) {
			this._searchSubject.next( '' );
		}
	}

	ngOnInit(): void {}

	ngOnDestroy(): void {
		this.search = null;
		this._searchSubject.next( '' );
		this._searchSubject.complete();
	}

	clearControlValue(): void {
		this.onInputChange( '' );
	}

	revertControlValue(): void {
		this.onInputChange( this._revertValue );
	}

	// is called by 'resetListOperations()' in superordinate listOps component via @ViewChild
	resetListOperator(): void {
		this.search = null;
		this._trimmedSearch = null;
		this._revertValue = null;
		this.showClearButton = false;
		this.showRevertButton = false;
		this.activeOperation = false;
		this.isFocused = false;
		this.showBody = false;  // to become obsolete
	}

	// to become obsolete
	toggleBodyVisibility(): void {
		this.showBody = !this.showBody;
		if ( this.showBody ) {
			this.searchInput?.nativeElement.focus();
			this.activeListOperationEmitter.emit( ListOperationsEnum.SEARCH );
		}
	}

	onInputChange( search: string ): void {
		this.search = search ?? '';
		this._trimmedSearch = this.search.trim();
		this.emitSearchChanges();
	}
}
